import React, { useEffect, useState } from 'react';
import { 
  Users as UsersIcon, 
  Shield, 
  Search,
  Filter,
  CheckCircle2,
  XCircle,
  Clock,
  DollarSign,
  Mail,
  User,
  X,
  Eye,
  Edit2,
  Trash2,
  Save,
  UserCheck,
  UserX,
  BookOpen
} from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import StatsCard from '@/components/admin/StatsCard';
import Modal from '@/components/admin/Modal';
import EnrollmentManager from '@/components/admin/EnrollmentManager';
import adminUsersService from '@/services/adminUsers.service';
import { authService } from '@/services';
import type { UserDetails, UserSearchFilters, UsersStats, UserUpdateData } from '@/services/adminUsers.service';

// Helper function to get display name from first_name/last_name
const getDisplayName = (user: UserDetails): string => {
  if (user.first_name || user.last_name) {
    return `${user.first_name || ''} ${user.last_name || ''}`.trim();
  }
  return user.username;
};

export default function AdminUsers() {
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [users, setUsers] = useState<UserDetails[]>([]);
  const [stats, setStats] = useState<UsersStats>({
    total_users: 0,
    active_users: 0,
    admin_users: 0,
    total_enrollments: 0,
    total_revenue: 0
  });
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Modal states
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [enrollmentManagerOpen, setEnrollmentManagerOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserDetails | null>(null);
  const [editForm, setEditForm] = useState<UserUpdateData>({});
  const [processing, setProcessing] = useState(false);
  
  // Search state
  const [simpleSearch, setSimpleSearch] = useState('');
  const [filters, setFilters] = useState<UserSearchFilters>({
    user_id: '',
    email: '',
    username: '',
    first_name: '',
    last_name: '',
    role: '',
    is_active: undefined
  });

  useEffect(() => {
    const user = authService.getCurrentUser();
    setCurrentUser(user);
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersData, statsData] = await Promise.all([
        adminUsersService.getAllUsers(),
        adminUsersService.getUsersStats()
      ]);
      
      if (!usersData.success) {
        throw new Error(usersData.error || 'Failed to fetch users');
      }
      if (!statsData.success) {
        throw new Error(statsData.error || 'Failed to fetch stats');
      }
      
      setUsers(usersData.data || []);
      setStats(statsData.data || {
        total_users: 0,
        active_users: 0,
        admin_users: 0,
        total_enrollments: 0,
        total_revenue: 0
      });
    } catch (error: any) {
      console.error('Error fetching data:', error);
      setError(error.message || 'Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSimpleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!simpleSearch.trim()) {
      fetchInitialData();
      return;
    }

    setSearching(true);
    setError(null);
    try {
      const response = await adminUsersService.searchUsers({
        search_query: simpleSearch.trim()
      });
      
      if (!response.success) {
        throw new Error(response.error || 'Search failed');
      }
      
      setUsers(response.data || []);
    } catch (error: any) {
      console.error('Error searching:', error);
      setError(error.message || 'Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleAdvancedSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const hasFilters = Object.entries(filters).some(([key, value]) => {
      if (key === 'is_active') return value !== undefined;
      return value && String(value).trim() !== '';
    });
    
    if (!hasFilters) {
      fetchInitialData();
      return;
    }

    setSearching(true);
    setError(null);
    try {
      const response = await adminUsersService.searchUsers(filters);
      
      if (!response.success) {
        throw new Error(response.error || 'Search failed');
      }
      
      setUsers(response.data || []);
    } catch (error: any) {
      console.error('Error searching:', error);
      setError(error.message || 'Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSimpleSearch('');
    setFilters({
      user_id: '',
      email: '',
      username: '',
      first_name: '',
    last_name: '',
      role: '',
      is_active: undefined
    });
    fetchInitialData();
  };

  const handleViewUser = (user: UserDetails) => {
    setSelectedUser(user);
    setViewModalOpen(true);
  };

  const handleEditUser = (user: UserDetails) => {
    setSelectedUser(user);
    setEditForm({
      username: user.username,
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
      is_active: user.is_active
    });
    setEditModalOpen(true);
  };

  const handleDeleteClick = (user: UserDetails) => {
    setSelectedUser(user);
    setDeleteModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedUser) return;

    setProcessing(true);
    setError(null);
    try {
      const response = await adminUsersService.updateUser(
        selectedUser.id,
        editForm
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to update user');
      }

      setSuccessMessage('User updated successfully!');
      setEditModalOpen(false);
      fetchInitialData();
      
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: any) {
      console.error('Error updating user:', error);
      setError(error.message || 'Failed to update user');
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedUser) return;

    setProcessing(true);
    setError(null);
    try {
      const response = await adminUsersService.deleteUser(selectedUser.id);

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete user');
      }

      setSuccessMessage('User deleted successfully!');
      setDeleteModalOpen(false);
      fetchInitialData();
      
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: any) {
      console.error('Error deleting user:', error);
      setError(error.message || 'Failed to delete user');
    } finally {
      setProcessing(false);
    }
  };

  const getRoleBadge = (role: string) => {
    const styles = {
      admin: 'bg-purple-500/20 border-purple-500/30 text-purple-400',
      user: 'bg-blue-500/20 border-blue-500/30 text-blue-400'
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${styles[role as keyof typeof styles] || styles.user}`}>
        {role.toUpperCase()}
      </span>
    );
  };

  const getStatusBadge = (isActive: boolean) => {
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${
        isActive 
          ? 'bg-green-500/20 border-green-500/30 text-green-400' 
          : 'bg-red-500/20 border-red-500/30 text-red-400'
      }`}>
        {isActive ? 'ACTIVE' : 'INACTIVE'}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <AdminLayout>
      <section className="min-h-screen bg-gradient-to-br from-purple-900 via-black to-blue-900 py-12 px-4">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">
                Users Management
              </h1>
              <p className="text-gray-400">View, manage, and moderate all user accounts</p>
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-400">
              <Shield className="w-4 h-4" />
              <span>Admin Access</span>
            </div>
          </div>

          {/* Success/Error Messages */}
          {successMessage && (
            <div className="bg-green-500/20 border border-green-500/30 text-green-400 px-6 py-4 rounded-lg flex items-center space-x-2">
              <CheckCircle2 className="w-5 h-5" />
              <span>{successMessage}</span>
            </div>
          )}

          {error && (
            <div className="bg-red-500/20 border border-red-500/30 text-red-400 px-6 py-4 rounded-lg flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <XCircle className="w-5 h-5" />
                <span>{error}</span>
              </div>
              <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
                <X className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <StatsCard
              label="Total Users"
              value={stats.total_users}
              icon={UsersIcon}
              iconColor="text-purple-400"
            />
            <StatsCard
              label="Active Users"
              value={stats.active_users}
              icon={UserCheck}
              iconColor="text-green-400"
            />
            <StatsCard
              label="Admins"
              value={stats.admin_users}
              icon={Shield}
              iconColor="text-purple-400"
            />
            <StatsCard
              label="Total Enrollments"
              value={stats.total_enrollments}
              icon={CheckCircle2}
              iconColor="text-blue-400"
            />
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/20">
              <div className="flex items-center justify-between mb-4">
                <DollarSign className="w-8 h-8 text-green-400" />
                <span className="text-3xl font-bold text-white">{formatCurrency(stats.total_revenue)}</span>
              </div>
              <h3 className="text-gray-400 text-sm">Total Revenue</h3>
            </div>
          </div>

          {/* Search Section */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center space-x-2">
                <Search className="w-6 h-6 text-purple-400" />
                <span>Search Users</span>
              </h2>
              <div className="flex space-x-4">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center space-x-2 ${
                    showFilters 
                      ? 'bg-purple-600 text-white' 
                      : 'bg-white/10 text-gray-400 hover:bg-white/20'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  <span>Advanced Filters</span>
                </button>
                <button
                  onClick={clearSearch}
                  className="px-4 py-2 bg-white/10 rounded-lg text-gray-400 hover:bg-white/20 transition-all"
                >
                  Clear
                </button>
              </div>
            </div>

            {/* Simple Search */}
            {!showFilters && (
              <form onSubmit={handleSimpleSearch} className="flex space-x-4">
                <input
                  type="text"
                  value={simpleSearch}
                  onChange={(e) => setSimpleSearch(e.target.value)}
                  placeholder="Search by name, username, or email..."
                  className="flex-1 px-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500/50 focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={searching}
                  className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg font-semibold hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {searching ? 'Searching...' : 'Search'}
                </button>
              </form>
            )}

            {/* Advanced Search */}
            {showFilters && (
              <form onSubmit={handleAdvancedSearch} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      <User className="w-4 h-4 inline mr-2" />
                      User ID
                    </label>
                    <input
                      type="text"
                      value={filters.user_id}
                      onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
                      placeholder="Enter user ID"
                      className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500/50 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      <User className="w-4 h-4 inline mr-2" />
                      Username
                    </label>
                    <input
                      type="text"
                      value={filters.username}
                      onChange={(e) => setFilters({ ...filters, username: e.target.value })}
                      placeholder="Enter username"
                      className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500/50 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      <Mail className="w-4 h-4 inline mr-2" />
                      Email
                    </label>
                    <input
                      type="text"
                      value={filters.email}
                      onChange={(e) => setFilters({ ...filters, email: e.target.value })}
                      placeholder="Enter email"
                      className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500/50 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      <User className="w-4 h-4 inline mr-2" />
                      First Name
                    </label>
                    <input
                      type="text"
                      value={filters.first_name}
                      onChange={(e) => setFilters({ ...filters, first_name: e.target.value })}
                      placeholder="Enter first name"
                      className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500/50 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      <User className="w-4 h-4 inline mr-2" />
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={filters.last_name}
                      onChange={(e) => setFilters({ ...filters, last_name: e.target.value })}
                      placeholder="Enter last name"
                      className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500/50 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      <Shield className="w-4 h-4 inline mr-2" />
                      Role
                    </label>
                    <select
                      value={filters.role}
                      onChange={(e) => setFilters({ ...filters, role: e.target.value })}
                      className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white focus:border-purple-500/50 focus:outline-none"
                    >
                      <option value="">All Roles</option>
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      <CheckCircle2 className="w-4 h-4 inline mr-2" />
                      Status
                    </label>
                    <select
                      value={filters.is_active === undefined ? '' : filters.is_active.toString()}
                      onChange={(e) => setFilters({ 
                        ...filters, 
                        is_active: e.target.value === '' ? undefined : e.target.value === 'true' 
                      })}
                      className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white focus:border-purple-500/50 focus:outline-none"
                    >
                      <option value="">All Statuses</option>
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={searching}
                  className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg font-semibold hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {searching ? 'Searching...' : 'Search'}
                </button>
              </form>
            )}
          </div>

          {/* Users Table */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
            <div className="p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white flex items-center space-x-2">
                <UsersIcon className="w-6 h-6 text-purple-400" />
                <span>Users ({users.length})</span>
              </h2>
            </div>

            {loading ? (
              <div className="p-12 text-center text-gray-400">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
                <p className="mt-4">Loading users...</p>
              </div>
            ) : users.length === 0 ? (
              <div className="p-12 text-center text-gray-400">
                <UsersIcon className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <p className="text-xl">No users found</p>
                <p className="text-sm mt-2">Try adjusting your search criteria</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">User</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Role</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Enrollments</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Joined</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-white/5 transition-colors">
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-white font-medium">{getDisplayName(user)}</div>
                            <div className="text-sm text-gray-400">@{user.username}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-white">{user.email}</div>
                        </td>
                        <td className="px-6 py-4">
                          {getRoleBadge(user.role)}
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(user.is_active)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-white font-semibold">{user.total_enrollments || 0}</div>
                          {user.total_spent && user.total_spent > 0 && (
                            <div className="text-xs text-gray-500">{formatCurrency(user.total_spent)} spent</div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-400">
                            <Clock className="w-3 h-3 inline mr-1" />
                            {formatDate(user.created_at)}
                          </div>
                          {user.last_login && (
                            <div className="text-xs text-gray-500 mt-1">
                              Last: {formatDate(user.last_login)}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleViewUser(user)}
                              className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditUser(user)}
                              className="p-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors"
                              title="Edit User"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(user)}
                              className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                              title="Delete User"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* View Details Modal */}
      <Modal
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        title="User Details"
      >
        {selectedUser && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-semibold text-gray-400 mb-2">Account Information</h3>
                <div className="space-y-2">
                  <p className="text-white"><span className="text-gray-400">First Name:</span> {selectedUser.first_name || '-'}</p>
                  <p className="text-white"><span className="text-gray-400">Last Name:</span> {selectedUser.last_name || '-'}</p>
                  <p className="text-white"><span className="text-gray-400">Username:</span> @{selectedUser.username}</p>
                  <p className="text-white"><span className="text-gray-400">Email:</span> {selectedUser.email}</p>
                  <p className="text-white"><span className="text-gray-400">User ID:</span> {selectedUser.id}</p>
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-semibold text-gray-400 mb-2">Status & Role</h3>
                <div className="space-y-2">
                  <p className="text-white"><span className="text-gray-400">Role:</span> {getRoleBadge(selectedUser.role)}</p>
                  <p className="text-white"><span className="text-gray-400">Status:</span> {getStatusBadge(selectedUser.is_active)}</p>
                  <p className="text-white"><span className="text-gray-400">Enrollments:</span> {selectedUser.total_enrollments || 0}</p>
                  {selectedUser.total_spent && selectedUser.total_spent > 0 && (
                    <p className="text-white"><span className="text-gray-400">Total Spent:</span> {formatCurrency(selectedUser.total_spent)}</p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setViewModalOpen(false);
                    setEnrollmentManagerOpen(true);
                  }}
                  className="mt-4 w-full px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg font-semibold hover:shadow-xl transition-all flex items-center justify-center space-x-2"
                >
                  <BookOpen className="w-4 h-4" />
                  <span>Manage Enrollments</span>
                </button>
              </div>
            </div>

            <div className="border-t border-white/10 pt-6">
              <h3 className="text-sm font-semibold text-gray-400 mb-2">Activity</h3>
              <div className="space-y-2">
                <p className="text-white"><span className="text-gray-400">Created:</span> {formatDate(selectedUser.created_at)}</p>
                <p className="text-white"><span className="text-gray-400">Updated:</span> {formatDate(selectedUser.updated_at)}</p>
                {selectedUser.last_login && (
                  <p className="text-white"><span className="text-gray-400">Last Login:</span> {formatDate(selectedUser.last_login)}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Edit User"
      >
        {selectedUser && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">Username</label>
              <input
                type="text"
                value={editForm.username || ''}
                onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white focus:border-purple-500/50 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">Email</label>
              <input
                type="email"
                value={editForm.email || ''}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white focus:border-purple-500/50 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">First Name</label>
              <input
                type="text"
                value={editForm.first_name || ''}
                onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white focus:border-purple-500/50 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">Last Name</label>
              <input
                type="text"
                value={editForm.last_name || ''}
                onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white focus:border-purple-500/50 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">Role</label>
              <select
                value={editForm.role || ''}
                onChange={(e) => setEditForm({ ...editForm, role: e.target.value as any })}
                className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white focus:border-purple-500/50 focus:outline-none"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_active"
                checked={editForm.is_active || false}
                onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                className="w-4 h-4 rounded border-white/10 bg-black/40 text-purple-600 focus:ring-purple-500"
              />
              <label htmlFor="is_active" className="text-white">Account Active</label>
            </div>

            <div className="flex space-x-4 pt-4">
              <button
                onClick={handleSaveEdit}
                disabled={processing}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg font-semibold hover:shadow-xl transition-all disabled:opacity-50 flex items-center justify-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>{processing ? 'Saving...' : 'Save Changes'}</span>
              </button>
              <button
                onClick={() => setEditModalOpen(false)}
                disabled={processing}
                className="px-6 py-3 bg-white/10 rounded-lg text-gray-400 hover:bg-white/20 transition-all disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Confirm Deletion"
      >
        {selectedUser && (
          <div className="space-y-6">
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <p className="text-red-400 mb-2">
                Are you sure you want to delete this user? This action cannot be undone and will permanently delete all user data, enrollments, and history.
              </p>
              <div className="text-sm text-gray-400 space-y-1 mt-4">
                <p><span className="font-semibold">Name:</span> {getDisplayName(selectedUser)}</p>
                <p><span className="font-semibold">Email:</span> {selectedUser.email}</p>
                <p><span className="font-semibold">Username:</span> @{selectedUser.username}</p>
                <p><span className="font-semibold">User ID:</span> {selectedUser.id}</p>
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={handleConfirmDelete}
                disabled={processing}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-red-600 to-red-800 rounded-lg font-semibold hover:shadow-xl transition-all disabled:opacity-50 flex items-center justify-center space-x-2"
              >
                <Trash2 className="w-4 h-4" />
                <span>{processing ? 'Deleting...' : 'Delete User'}</span>
              </button>
              <button
                onClick={() => setDeleteModalOpen(false)}
                disabled={processing}
                className="px-6 py-3 bg-white/10 rounded-lg text-gray-400 hover:bg-white/20 transition-all disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </Modal>
      {/* Enrollment Manager Modal */}
      {enrollmentManagerOpen && selectedUser && (
        <EnrollmentManager
          userId={selectedUser.id}
          userName={getDisplayName(selectedUser)}
          onClose={() => setEnrollmentManagerOpen(false)}
        />
      )}
    </AdminLayout>
  );
}
